import { useEffect } from 'react';
import { LoggerProvider, BatchLogRecordProcessor } from '@opentelemetry/sdk-logs';
import { OTLPLogExporter } from '@opentelemetry/exporter-logs-otlp-http';
import type * as logsAPI from '@opentelemetry/api-logs';
import { userAttributes, getTraceContext, forceFlushTraces } from './instrumentation';
import { getResource } from './resource';
import { setupHttpLogger } from './httpLogger';
import { allowLog } from './logThrottle';
import axiosVortex from '../../utils/axios.instance';
import { apiFlowBackend as apiFlowBackendServices, waveBackend } from '../flowBackendServices';
import { apiFlowBackend as apiFlowBackendApi } from '../flowBackendApi';

// Defensive secondary filter — catches our wrapper frame when the source path
// or function name survives bundling. Minifiers like Terser strip function
// names by default, so the primary mechanism is positional (skipWrapper).
const LOGGER_FRAME_REGEX = /consolelogger/i;

// `stack[0]` is the "Error" header. When the stack came from `new Error()`
// inside our console wrapper, `stack[1]` is the wrapper itself — pass
// skipWrapper=true to bypass it. For external stacks (window.onerror,
// unhandledrejection), `stack[1]` is the actual throw site — keep the default.
function extractCaller(stack: string | undefined, skipWrapper = false): string {
  if (!stack) return 'unknown';
  const lines = stack.split('\n');
  for (let i = skipWrapper ? 2 : 1; i < lines.length; i++) {
    const line = lines[i]?.trim();
    if (line && !LOGGER_FRAME_REGEX.test(line)) {
      return line.replace(/^\s*at\s+/, '');
    }
  }
  return 'unknown';
}

const BEARER_REGEX = /Bearer\s+[A-Za-z0-9\-._~+/]+=*/g;
const GENERIC_TOKEN_REGEX = /eyJ[A-Za-z0-9\-._~+/]+={0,2}/g;

function maskTokens(str: string): string {
  return str
    .replace(BEARER_REGEX, (match) => 'Bearer *****' + match.slice(-4))
    .replace(GENERIC_TOKEN_REGEX, (match) => '*****' + match.slice(-4));
}

function formatArg(arg: unknown): string {
  if (arg instanceof Error) {
    const axiosErr = arg as any;
    if (axiosErr.isAxiosError || axiosErr.name === 'AxiosError') {
      return JSON.stringify({
        name: axiosErr.name,
        message: axiosErr.message,
        code: axiosErr.code,
        status: axiosErr.response?.status || axiosErr.status,
        url: axiosErr.config?.url,
        method: axiosErr.config?.method?.toUpperCase(),
      });
    }
    return `${arg.name}: ${arg.message}`;
  }
  if (typeof arg === 'object' && arg !== null) {
    try {
      return maskTokens(JSON.stringify(arg));
    } catch {
      return '[unserializable]';
    }
  }
  return String(arg);
}

type LogSource = 'browser-console' | 'window-error' | 'unhandled-rejection';
type LogType = 'ConsoleLog' | 'WindowError' | 'UnhandledRejection';

interface EmitParams {
  level: 'warn' | 'error';
  source: LogSource;
  type: LogType;
  msg: string;
  errorType: string;
  errorStack: string;
  caller: string;
}

function createEmit(logger: logsAPI.Logger) {
  let inLog = false;
  return (p: EmitParams): void => {
    if (inLog) return;
    inLog = true;
    try {
      // Flood control: collapse repeats / cap rate. The native console call
      // already ran in the wrapper, so this only drops the OTel emit.
      if (!allowLog(`${p.level}|${p.type}|${p.msg}`)) return;
      const { traceID, spanID } = getTraceContext();
      const clientCode = userAttributes.clientCode || '';
      const applicationId = process.env.REACT_APP_TV_APPLICATION_ID || '';
      const env = process.env.REACT_APP_ENV || '';
      const pageUrl = window.location.pathname;

      const body = {
        level: p.level,
        time: new Date().toISOString(),
        caller: p.caller,
        msg: maskTokens(p.msg),
        traceID,
        spanID,
        clientCode,
        applicationId,
        env,
        type: p.type,
        errorType: p.errorType,
        pageUrl,
      };

      // traceID/spanID are intentionally omitted from attributes — when an
      // active span exists, the OTel SDK auto-attaches `trace_id`/`span_id` to
      // the LogRecord. For non-span logs (most console errors), only the body
      // carries the session-stable ID, which is correct: it stops Grafana from
      // surfacing a Tempo link that would 404 on a synthetic ID.
      logger.emit({
        severityText: p.level.toUpperCase(),
        body: JSON.stringify(body),
        attributes: {
          source: p.source,
          level: p.level,
          type: p.type,
          clientCode,
          applicationId,
          env,
          errorType: p.errorType,
          caller: p.caller,
          pageUrl,
          stackTrace: maskTokens(p.errorStack),
          ...userAttributes,
        },
      });
    } catch {
      // Logging must never break application flow
    } finally {
      inLog = false;
    }
  };
}

// ----- domain logger --------------------------------------------------------
// The LoggerProvider below lives inside ConsoleLogger's effect and was never
// reachable from application code, so chart code could only reach Loki by throwing
// or calling console.warn — neither of which produces a queryable event. chartLog
// shares the same provider, exporter and resource: no second endpoint. Records are
// shaped to match mobile_charts' emitLog so one dashboard covers both products.
let _domainLogger: logsAPI.Logger | null = null;

// A defect can be detected before the effect above has run — the boot layout load
// happens early. Buffer a handful of records rather than lose them.
const MAX_PENDING = 20;
let _pending: Array<{ level: string; event: string; attrs: Record<string, any> }> = [];

function emitDomain(logger: logsAPI.Logger, level: string, event: string, attrs: Record<string, any>): void {
  try {
    const { traceID, spanID } = getTraceContext();
    const clientCode = userAttributes.clientCode || '';
    const env = process.env.REACT_APP_ENV || '';
    const body = {
      level,
      time: new Date().toISOString(),
      event,
      surface: 'flow_web',
      traceID,
      spanID,
      clientCode,
      applicationId: process.env.REACT_APP_TV_APPLICATION_ID || '',
      env,
      pageUrl: window.location.pathname,
      ...attrs,
    };
    const attributes: Record<string, any> = {
      event,
      level,
      surface: 'flow_web',
      source: 'chart',
      clientCode,
      env,
    };
    // CLOSED enum (see vendor/chart-boot-watchdog.js), so indexing is safe and an
    // alert can fire per cause.
    if (attrs && attrs.reason) { attributes.reason = String(attrs.reason); }
    logger.emit({
      severityText: level.toUpperCase(),
      body: JSON.stringify(body),
      attributes,
    });
  } catch {
    // Telemetry must never break the chart.
  }
}

/**
 * Emit a chart domain event to Loki.
 *
 * PROBLEMS ONLY — anything below `warn` is dropped, matching the severity floor in
 * mobile_charts' lib/telemetry/logger.js. Structural rather than conventional: a
 * future chartLog('info', ...) cannot flood the log store, it simply does nothing.
 * It costs no observability, since a failure rate's denominator is in Tempo.
 *
 * @param level 'warn' | 'error' ('info'/'debug' are intentionally no-ops)
 * @param event machine-readable name, e.g. "chart.load_failed"
 * @param attrs domain fields; `reason` becomes an indexed label
 */
export function chartLog(level: string, event: string, attrs: Record<string, any> = {}): void {
  if (level !== 'warn' && level !== 'error') { return; }
  if (!_domainLogger) {
    if (_pending.length < MAX_PENDING) { _pending.push({ level, event, attrs }); }
    return;
  }
  emitDomain(_domainLogger, level, event, attrs);
}

const ConsoleLogger = () => {
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const loggerProvider = new LoggerProvider({ resource: getResource() });
    const exporter = new OTLPLogExporter({ url: 'https://tracer.rupeezy.in/v1/logs' });
    loggerProvider.addLogRecordProcessor(new BatchLogRecordProcessor(exporter));
    const logger: logsAPI.Logger = loggerProvider.getLogger('browser-console-logger');
    const emit = createEmit(logger);

    // Make the provider reachable for domain events, and drain anything that was
    // emitted before this effect ran.
    _domainLogger = loggerProvider.getLogger('chart-domain-logger');
    if (_pending.length) {
      const drain = _pending;
      _pending = [];
      drain.forEach((r) => emitDomain(_domainLogger as logsAPI.Logger, r.level, r.event, r.attrs));
    }

    const levels = ['warn', 'error'] as const;
    const originalConsole: Partial<Record<(typeof levels)[number], (...args: any[]) => void>> = {};
    levels.forEach((level) => {
      const original = console[level];
      originalConsole[level] = original;
      // Named function expression so the wrapper frame shows up as
      // `consoleLoggerEmit` in stacks (CRA-style bundlers otherwise label
      // arrow-functions assigned to computed properties as `console.<computed>`,
      // which the LOGGER_FRAME_REGEX cannot detect).
      console[level] = function consoleLoggerEmit(...args: any[]) {
        original.apply(console, args);
        const stack = new Error().stack;
        let errorType = 'GeneralError';
        let errorStack = '';
        for (const arg of args) {
          if (arg instanceof Error) {
            errorType = arg.name;
            errorStack = arg.stack || '';
            break;
          }
        }
        emit({
          level,
          source: 'browser-console',
          type: 'ConsoleLog',
          msg: args.map(formatArg).join(' '),
          errorType,
          errorStack: errorStack || stack || '',
          caller: extractCaller(stack, true),
        });
      } as typeof original;
    });

    const onWindowError = (ev: ErrorEvent) => {
      const err = ev.error instanceof Error ? ev.error : null;
      const msg = ev.message || (err ? `${err.name}: ${err.message}` : 'WindowError');
      emit({
        level: 'error',
        source: 'window-error',
        type: 'WindowError',
        msg,
        errorType: err?.name || 'WindowError',
        errorStack: err?.stack || '',
        caller: ev.filename ? `${ev.filename}:${ev.lineno}:${ev.colno}` : extractCaller(err?.stack),
      });
    };

    const onUnhandledRejection = (ev: PromiseRejectionEvent) => {
      const reason = ev.reason;
      const err = reason instanceof Error ? reason : null;
      const msg = err ? `${err.name}: ${err.message}` : formatArg(reason);
      emit({
        level: 'error',
        source: 'unhandled-rejection',
        type: 'UnhandledRejection',
        msg,
        errorType: err?.name || 'UnhandledRejection',
        errorStack: err?.stack || '',
        caller: extractCaller(err?.stack),
      });
    };

    window.addEventListener('error', onWindowError);
    window.addEventListener('unhandledrejection', onUnhandledRejection);

    const ejectHttpLogger = setupHttpLogger(logger, axiosVortex, apiFlowBackendServices, waveBackend, apiFlowBackendApi);

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        loggerProvider.forceFlush().catch(() => {});
        forceFlushTraces().catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.removeEventListener('error', onWindowError);
      window.removeEventListener('unhandledrejection', onUnhandledRejection);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      ejectHttpLogger();
      levels.forEach((level) => {
        if (originalConsole[level]) console[level] = originalConsole[level]!;
      });
    };
  }, []);

  return null;
};

export default ConsoleLogger;
